import { AgentTypes, EventTypes } from '../../../constants/event';
import { MOD_SEPARATOR } from '../../../utils/constants';
import logger from '../../../utils/logger';
import {
  pickPromptPhrase,
  splitPromptPhrases,
} from '../../../utils/promptPhrases';
import { EXPERIMENT_ASSIGNED_CODE } from './experiments.constants';
import {
  IExperimentAssignment,
  IPromptSlot,
  IUpsellExperimentStamp,
} from './experiments.props';

const toConstraintKey = (constraintName?: string | null) =>
  (constraintName ?? '').toLowerCase();

// A slot with no cart item is keyed by its constraint alone, for the whole car.
const toPromptSlotKey = ({
  cartItemId,
  pathToItem,
  constraintName,
}: IPromptSlot) =>
  cartItemId !== undefined && pathToItem
    ? [cartItemId, pathToItem].join(MOD_SEPARATOR)
    : toConstraintKey(constraintName);

// A slash command asks no cart slot, so its prompt is counted per constraint.
const slashCommandPromptSlot = (item: {
  name: string;
  activeConstraintName?: string;
}): IPromptSlot => ({ constraintName: item.activeConstraintName ?? item.name });

// The car the console is working: the task-router session while a task is current, else the session-boundary one.
const currentCarSessionId = ({
  currentTaskId,
  taskAssignedSessionId,
  sessionBoundarySessionId,
}: {
  currentTaskId?: string;
  taskAssignedSessionId?: string;
  sessionBoundarySessionId?: string;
}) => (currentTaskId ? taskAssignedSessionId : sessionBoundarySessionId) || '';

const isExperimentAssignedEvent = (message: any) =>
  message?.event === EventTypes.info &&
  message.data?.type === 'METRIC' &&
  message.data?.code === EXPERIMENT_ASSIGNED_CODE;

// Returns the assignment carried by an EXPERIMENT_ASSIGNED metric or a hypothesis frame, else null.
const extractExperimentAssignment = (
  message: any
): IExperimentAssignment | null => {
  if (message?.event === EventTypes.hypothesis) {
    return message.data?.experiments ?? null;
  }
  if (isExperimentAssignedEvent(message)) {
    return message.data?.metadata?.experiment_assignment ?? null;
  }
  return null;
};

const isConstraintWithheld = (
  withheldConstraints: Set<string>,
  constraintName?: string | null
) =>
  withheldConstraints.size > 0 &&
  withheldConstraints.has(toConstraintKey(constraintName));

// `path` names the ask path that skipped it, e.g. 'mod-group heading'.
const logWithheldPromptSkipped = (
  constraintName: string | null | undefined,
  path: string
) => logger.info(`Withheld prompt skipped: ${constraintName} (${path})`);

const getPromptOverride = (
  prompts: Record<string, string>,
  constraintName?: string | null
): string | undefined => prompts[toConstraintKey(constraintName)] || undefined;

// The phrases a constraint's prompt rotates through: the session override's, else the menu's.
const getSpokenPromptPhrases = (
  prompts: Record<string, string>,
  constraintName: string | null | undefined,
  menuPrompt: string | string[] | undefined
): string[] => {
  const override = getPromptOverride(prompts, constraintName);
  if (override) return splitPromptPhrases(override);
  return Array.isArray(menuPrompt)
    ? menuPrompt
    : splitPromptPhrases(menuPrompt);
};

// This ask's pick for a constraint's prompt; see pickPromptPhrase.
const getSpokenPromptPick = (
  prompts: Record<string, string>,
  constraintName: string | null | undefined,
  menuPrompt: string | string[] | undefined,
  askIndex: number | undefined,
  hasCheckTotal: boolean
) =>
  pickPromptPhrase(
    getSpokenPromptPhrases(prompts, constraintName, menuPrompt),
    askIndex,
    hasCheckTotal
  );

// What to speak for a constraint's prompt on this ask: one phrase per ask, the first on the first.
const getSpokenPrompt = (
  prompts: Record<string, string>,
  constraintName: string | null | undefined,
  menuPrompt: string | string[] | undefined,
  askIndex: number | undefined,
  hasCheckTotal: boolean
): string =>
  getSpokenPromptPick(
    prompts,
    constraintName,
    menuPrompt,
    askIndex,
    hasCheckTotal
  )?.phrase ?? '';

// What a slash-command item speaks on its next ask; undefined when it has no prompt.
const getSlashCommandSpokenPrompt = (
  prompts: Record<string, string>,
  promptAskCounts: Record<string, number>,
  item: {
    name: string;
    activeConstraintName?: string;
    slashCommands?: string[];
  },
  hasCheckTotal: boolean
): string | undefined =>
  item.slashCommands?.length
    ? getSpokenPrompt(
        prompts,
        item.activeConstraintName,
        item.slashCommands,
        promptAskCounts[toPromptSlotKey(slashCommandPromptSlot(item))],
        hasCheckTotal
      )
    : undefined;

// The AI's own upsell metric labels for this constraint; unknown until an assignment arrives.
const buildUpsellExperimentStamp = (
  assignment: IExperimentAssignment | null,
  constraintName?: string | null
): IUpsellExperimentStamp =>
  assignment
    ? {
        ...assignment.upsell_metric_fields?.[toConstraintKey(constraintName)],
      }
    : { experiments_unknown: true };

// HITL-1761: the constraint of an upsell the AI spoke itself (its LLM METRIC
// info carrying `upsell_attempted`), as a constraint key, or null.
const extractAIUpsellIntent = (message: any): string | null => {
  if (
    message?.event !== EventTypes.info ||
    message?.agent_type !== AgentTypes.llm
  ) {
    return null;
  }
  const intent = message?.data?.metadata?.upsell_attempted?.upsell_intent;
  return typeof intent === 'string' && intent
    ? toConstraintKey(intent.replace(/^\//, ''))
    : null;
};

export {
  toConstraintKey,
  toPromptSlotKey,
  slashCommandPromptSlot,
  currentCarSessionId,
  isExperimentAssignedEvent,
  extractExperimentAssignment,
  extractAIUpsellIntent,
  isConstraintWithheld,
  logWithheldPromptSkipped,
  getSpokenPromptPhrases,
  getSpokenPromptPick,
  getSpokenPrompt,
  getSlashCommandSpokenPrompt,
  buildUpsellExperimentStamp,
};
